// French copy, namespace `emails` (source language: English is typed against it).
// Product emails (shared/lib/product-email-templates.ts), account emails
// (shared/lib/transactional-email-templates.ts) and their shared layout
// (shared/lib/email-layout.ts). Text only: the templates escape it.

/** Text with a link in the middle: `before` + link + `after`. */
type LinkedText = { before: string; link: string; after: string };

const plural = (count: number, one: string, many: string) => `${count} ${count > 1 ? many : one}`;

export const emails = {
  layout: {
    greeting: (firstName: string | null) => (firstName ? `Bonjour ${firstName},` : "Bonjour,"),
    signature: "L’équipe Hune",
    legalLine: (entity: { brand: string; name: string; address: string }) =>
      `${entity.brand}, édité par ${entity.name} SAS, ${entity.address}.`,
    /** A label followed by its value (an address, a signal), in the text version. */
    labeled: (label: string, value: string) => `${label} : ${value}`,
    /** The same, the value on its own line (a long link). */
    labeledBlock: (label: string, value: string) => `${label} :\n${value}`,
    fallbackLink: "Le bouton ne s’ouvre pas ? Copiez cette adresse dans votre navigateur :",
    settings: "Paramètres",
  },
  passwordReset: {
    subject: "Réinitialisez votre mot de passe Hune",
    preheader: "Le lien est valable une heure.",
    title: "Choisissez un nouveau mot de passe",
    intro: "Vous avez demandé à changer le mot de passe de votre compte Hune. Ce lien est valable une heure.",
    action: "Choisir un nouveau mot de passe",
    ignore: "Si vous n’êtes pas à l’origine de cette demande, ignorez ce message : votre mot de passe reste inchangé.",
    why: "Vous recevez cet e-mail parce qu’une réinitialisation du mot de passe a été demandée pour votre compte.",
  },
  invitation: {
    subject: "Votre compte Hune est prêt",
    preheader: "Choisissez votre mot de passe pour accéder à Hune.",
    title: "Votre compte est prêt",
    intro:
      "Un compte Hune vient d’être créé pour vous. Il ne reste qu’à choisir votre mot de passe : le lien est valable une heure.",
    action: "Choisir mon mot de passe",
    expired: {
      before: "Passé ce délai, utilisez « Mot de passe oublié » sur la ",
      link: "page de connexion",
      after: ".",
    } satisfies LinkedText,
    expiredText: (loginUrl: string) =>
      `Passé ce délai, utilisez « Mot de passe oublié » sur la page de connexion : ${loginUrl}`,
    why: "Vous recevez cet e-mail parce qu’un administrateur de Hune a créé un compte à cette adresse.",
  },
  welcome: {
    started: {
      subject: "Votre agent Hune est au travail",
      title: "Votre agent est au travail",
      preheader: "Le premier passage prend une dizaine de minutes. Vous n’avez rien à faire.",
      opening: "L’agent a déjà commencé son premier passage. Comptez une dizaine de minutes : vous n’avez rien à faire.",
      stepsIntro: "Pendant ce temps :",
      action: "Suivre l’agent",
    },
    notStarted: {
      subject: "Votre espace Hune est prêt",
      title: "Votre espace est prêt",
      preheader: "Lancez la première analyse depuis la Vue d’ensemble.",
      opening:
        "La première analyse n’a pas pu démarrer d’elle-même. Lancez-la depuis la Vue d’ensemble : comptez ensuite une dizaine de minutes, sans rien d’autre à faire.",
      stepsIntro: "Une fois lancée :",
      action: "Lancer la première analyse",
    },
    /** The title, then the opening, as the first paragraph of the text version. */
    lead: (title: string, opening: string) => `${title}. ${opening}`,
    steps: {
      readSite: (site: string | null) => `Il lit le site de votre offre${site ? ` (${site})` : ""} et en déduit votre cible.`,
      search: "Il cherche sur le web les organisations qui montrent un signal d’achat, daté et sourcé.",
      pipeline: "Les leads arrivent dans votre pipeline, chacun avec son signal et les interlocuteurs à contacter.",
    },
    targetingText: "Ciblage à côté ? Corrigez-le dans « Mes offres » : le passage suivant en tient compte.",
    targeting: {
      before: "Ciblage à côté ? Corrigez-le dans ",
      link: "« Mes offres »",
      after: " : le passage suivant en tient compte.",
    } satisfies LinkedText,
    recap:
      "Ensuite, la veille repasse d’elle-même. Vous recevrez un récapitulatif une fois par semaine au plus, et seulement si l’agent a trouvé du nouveau.",
    why: "Vous recevez cet e-mail parce que vous venez de créer votre espace Hune ; il n’y en aura pas d’autre de ce genre.",
    unsubscribeText: (url: string) => `Pour ne pas recevoir le récapitulatif hebdomadaire : ${url}`,
    unsubscribeLink: "Ne pas recevoir le récapitulatif hebdomadaire",
  },
  digest: {
    /** « 3 nouveaux leads et 1 réactivé », « 1 nouveau lead », « 2 leads réactivés ». */
    counts: (newLeads: number, reactivated: number) => {
      const parts: string[] = [];
      if (newLeads > 0) parts.push(plural(newLeads, "nouveau lead", "nouveaux leads"));
      if (reactivated > 0) {
        const noun = reactivated > 1 ? "réactivés" : "réactivé";
        parts.push(newLeads > 0 ? `${reactivated} ${noun}` : `${plural(reactivated, "lead", "leads")} ${noun}`);
      }
      return parts.join(" et ");
    },
    /** « 3 nouveaux, 1 réactivé » : the count line of an offer. */
    offerCounts: (newLeads: number, reactivated: number) => {
      const parts: string[] = [];
      if (newLeads > 0) parts.push(plural(newLeads, "nouveau", "nouveaux"));
      if (reactivated > 0) parts.push(plural(reactivated, "réactivé", "réactivés"));
      return parts.join(", ");
    },
    /** Subject and title never name an offer: its name is text the account wrote. */
    subject: (totals: string, sinceLastDigest: boolean, severalOffers: boolean) =>
      `${totals} ${sinceLastDigest ? "depuis le dernier récapitulatif" : "en sept jours"}${severalOffers ? " sur vos offres" : ""}`,
    title: (totals: string, sinceLastDigest: boolean) =>
      `${totals} ${sinceLastDigest ? "depuis le dernier récapitulatif" : "en sept jours"}`,
    introText: (sinceLastDigest: boolean, totals: string, offer: { single: string } | { count: number }) => {
      const period = sinceLastDigest ? "Depuis le dernier récapitulatif" : "Ces sept derniers jours";
      return "single" in offer
        ? `${period}, l’agent a trouvé du nouveau pour ${offer.single} : ${totals}.`
        : `${period}, l’agent a trouvé du nouveau sur ${offer.count} de vos offres : ${totals} en tout.`;
    },
    introHtml: (sinceLastDigest: boolean, offer: { single: string } | { count: number }) => {
      const period = sinceLastDigest ? "Depuis le dernier récapitulatif" : "Ces sept derniers jours";
      return "single" in offer
        ? `${period}, l’agent a trouvé du nouveau pour ${offer.single}. Voici les leads à regarder d’abord.`
        : `${period}, l’agent a trouvé du nouveau sur ${offer.count} de vos offres. Voici, pour chacune, les leads à regarder d’abord.`;
    },
    reactivatedNote: "Un lead réactivé est un lead que vous aviez écarté et qui émet un nouveau signal.",
    quiet: (sinceLastDigest: boolean, offers: string[]) =>
      `Rien de nouveau ${sinceLastDigest ? "depuis" : "ces sept derniers jours"} sur ${offers.join(", ")} : la veille continue.`,
    reactivated: "réactivé",
    seeLeads: (count: number) => (count > 1 ? `Voir les ${count} leads` : "Voir le lead"),
    seeOfferLeads: (count: number) => (count > 1 ? `Voir les ${count} leads de cette offre` : "Voir le lead de cette offre"),
    openHune: "Ouvrir Hune",
    others: (count: number) => `Et ${plural(count, "autre lead", "autres leads")} dans votre pipeline.`,
    nextPass: (day: string) => `Prochain passage : ${day}`,
    /** Name of an offer without a usable name or address. */
    yourOffer: "votre offre",
    why: "Vous recevez ce récapitulatif parce que l’agent a trouvé du nouveau sur vos offres. Il arrive une fois par semaine au plus, et jamais quand il n’y a rien.",
    unsubscribeText: (url: string, settingsUrl: string) =>
      `Ne plus le recevoir : ${url} (ou depuis vos paramètres : ${settingsUrl}).`,
    unsubscribeLink: "Ne plus le recevoir",
    /** `totals` arrives capitalized. */
    preheader: (totals: string, offerCount: number) =>
      offerCount > 1
        ? `${totals} sur ${offerCount} offres, avec leur signal et leur source.`
        : `${totals}, avec leur signal et leur source.`,
  },
};
